import { useEffect, useMemo, useState } from "react";

const EXTENSION_HANDSHAKE_TIMEOUT_MS = 1200;
const EXTENSION_SOURCE = "elitphone-extension";
const WEB_APP_SOURCE = "elitphone-web";
const EXTENSION_PING_TYPE = "ELITPHONE_EXTENSION_PING";
const EXTENSION_PONG_TYPE = "ELITPHONE_EXTENSION_PONG";

enum ExtensionStatus {
    CHECKING = "checking",
    INSTALLED = "installed",
    NOT_INSTALLED = "not_installed",
}

type ExtensionMessagePayload = {
    source?: string;
    type?: string;
    installed?: boolean;
};

export function useChromeExtensionStatus() {
    const [status, setStatus] = useState<ExtensionStatus>(ExtensionStatus.CHECKING);

    useEffect(() => {
        let resolved = false;

        const handleMessage = (event: MessageEvent<ExtensionMessagePayload>) => {
            const payload = event.data;
            if (!payload || payload.source !== EXTENSION_SOURCE) {
                return;
            }

            if (payload.installed === true || payload.type === EXTENSION_PONG_TYPE) {
                resolved = true;
                setStatus(ExtensionStatus.INSTALLED);
            }
        };

        window.addEventListener("message", handleMessage);

        window.postMessage(
            {
                source: WEB_APP_SOURCE,
                type: EXTENSION_PING_TYPE,
            } satisfies ExtensionMessagePayload,
            window.location.origin
        );

        const timeoutId = window.setTimeout(() => {
            if (!resolved) {
                setStatus(ExtensionStatus.NOT_INSTALLED);
            }
        }, EXTENSION_HANDSHAKE_TIMEOUT_MS);

        return () => {
            window.clearTimeout(timeoutId);
            window.removeEventListener("message", handleMessage);
        };
    }, []);

    return useMemo(
        () => ({
            status,
            isInstalled: status === ExtensionStatus.INSTALLED,
        }),
        [status]
    );
}
